import { Button, Dialog } from '@mui/material';
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation, Trans } from 'react-i18next';
import RouterWrapper from '../../../../utils/RouterWrapper';
import SearchStore from '../../../../stores/SearchStore';
import Currencies from '../../../../utils/Currencies';
import { RESERVATION_DIALOG_VARIANT } from '../helpers/index';
import ReservationActions from '../../../../actions/ReservationActions';
import HotelActions from '../../../../actions/HotelActions';
import { withRouter } from '../../../../utils/withRouter';
import companyIds from 'arbitrip-common/theme/mapping';
import { COMPANY_NAME } from "arbitrip-common/theme/constants";

class ReservationDialog extends React.Component {
	goToHotel() {
		const { hotel, navigate, deal } = this.props;
		ReservationActions.clearReservation();
		HotelActions.banDeal(hotel, deal);
		ReservationActions.resetOutOfPolicyRequestStatus();
		const search_terms = SearchStore.getSearchTerms();
		const token = SearchStore.getToken();
		RouterWrapper.goToHotelPage(navigate, hotel, token, search_terms);
	}

	getTitleText() {
		const { variant, profile, t } = this.props;
		const tripManagerName = (profile && profile.approver_full_name) || t('ReservationDialog.DefaultTripManager');

		switch (variant) {
			case RESERVATION_DIALOG_VARIANT.SUCCESS:
				return t('ReservationDialog.SuccessTitle');
			case RESERVATION_DIALOG_VARIANT.FAILED:
				return t('ReservationDialog.FailedTitle');
			case RESERVATION_DIALOG_VARIANT.ROOM_NOT_AVAILABLE:
				return t('ReservationDialog.RoomNotAvailableTitle');
			case RESERVATION_DIALOG_VARIANT.PENDING:
				return t('ReservationDialog.PendingTitle');
			case RESERVATION_DIALOG_VARIANT.PRICE_CHANGED:
				return t('ReservationDialog.PriceChangedTitle');
			case RESERVATION_DIALOG_VARIANT.QUOTE:
				return t('ReservationDialog.QuoteTitle');
			case RESERVATION_DIALOG_VARIANT.OUT_OF_POLICY:
				return (
					<Trans
						i18nKey="ReservationDialog.OutOfPolicyTitle"
						values={{ tripManagerName }}
						components={[<b />]}
					/>
				);

			default:
				return null;
		}
	}

	getMainMessageComponent() {
		const { variant, shortId, oldPrice, newPrice, profile, tripName, t } = this.props;

		switch (variant) {
			case RESERVATION_DIALOG_VARIANT.SUCCESS:
				return (
					<p className="message">
						<Trans
							i18nKey="ReservationDialog.SuccessMessage"
							values={{ shortId }}
							components={[<b />, <br />]}
						/>
					</p>
				);
			case RESERVATION_DIALOG_VARIANT.OUT_OF_POLICY:
				return <p className="message">{t('ReservationDialog.OutOfPolicyMessage')}</p>;
			case RESERVATION_DIALOG_VARIANT.QUOTE:
				return (
					<p className="message">
						<Trans i18nKey="ReservationDialog.QuoteMessage" values={{ tripName }} components={[<b />]} />
					</p>
				);
			case RESERVATION_DIALOG_VARIANT.FAILED:
				return (
					<p className="message">
						<Trans i18nKey="ReservationDialog.FailedMessage" components={[<br />]} />
					</p>
				);
			case RESERVATION_DIALOG_VARIANT.ROOM_NOT_AVAILABLE:
				return (
					<p className="message">
						<Trans i18nKey="ReservationDialog.RoomNotAvailableMessage" />
					</p>
				);
			case RESERVATION_DIALOG_VARIANT.PENDING:
				return (
					<p className="message">
						<Trans i18nKey="ReservationDialog.PendingMessage" components={[<br />]} />
					</p>
				);
			case RESERVATION_DIALOG_VARIANT.PRICE_CHANGED:
				const value = Math.round(newPrice) - Math.round(oldPrice) || 1;
				const textValue = Currencies.getPriceWithCurrency(value, profile.company_currency);
				return (
					<p className="message">
						<Trans
							i18nKey="ReservationDialog.PriceChangedMessage"
							values={{ textValue }}
							components={[<b />]}
						/>
					</p>
				);
			default:
				return null;
		}
	}

	getAdditionalMessageComponent() {
		const { variant, oldPrice, newPrice, profile, isNonRefundable, t } = this.props;

		switch (variant) {
			case RESERVATION_DIALOG_VARIANT.SUCCESS:
			case RESERVATION_DIALOG_VARIANT.FAILED:
			case RESERVATION_DIALOG_VARIANT.QUOTE:
			case RESERVATION_DIALOG_VARIANT.ROOM_NOT_AVAILABLE:
			case RESERVATION_DIALOG_VARIANT.OUT_OF_POLICY:
				return <div className="additional-placeholder" />;
			case RESERVATION_DIALOG_VARIANT.PENDING:
				return isNonRefundable ? (
					<div className="additional-message">
						<div className="additional-message-text">
							<Trans
								i18nKey="ReservationDialog.PendingAdditionalMessage"
								values={{ nonRefundable: t('ReservationDialog.NonRefundable') }}
								components={[<b />, <br />]}
							/>
						</div>
					</div>
				) : (
					<div className="additional-placeholder" />
				);
			case RESERVATION_DIALOG_VARIANT.PRICE_CHANGED:
				const oldPriceText = Currencies.getPriceWithCurrency(Math.round(oldPrice), profile.company_currency);
				const newPriceText = Currencies.getPriceWithCurrency(Math.round(newPrice), profile.company_currency);
				return (
					<div className="price-changed-container flex">
						<div>
							<div>{t('ReservationDialog.OldPriceLabel')}</div>
							<div className="price">{oldPriceText}</div>
						</div>
						<div className="right-arrow"></div>
						<div>
							<div>{t('ReservationDialog.NewPriceLabel')}</div>
							<div className="price">{newPriceText}</div>
						</div>
					</div>
				);
			default:
				return null;
		}
	}

	getActionButtonsComponent() {
		const { variant, primeButtonAction, t } = this.props;

		switch (variant) {
			case RESERVATION_DIALOG_VARIANT.SUCCESS:
			case RESERVATION_DIALOG_VARIANT.PENDING:
			case RESERVATION_DIALOG_VARIANT.OUT_OF_POLICY:
				return (
					<Button
						className="button-primary"
						key={'close-button'}
						variant="text"
						color="primary"
						onClick={primeButtonAction}
						data-testid="close-reservation-dialog"
					>
						{t('ReservationDialog.CloseButton')}
					</Button>
				);
			case RESERVATION_DIALOG_VARIANT.QUOTE:
				return (
					<Button
						className="button-primary"
						key={'close-button'}
						variant="text"
						color="primary"
						onClick={primeButtonAction}
					>
						{t('ReservationDialog.ViewTripButton')}
					</Button>
				);
			case RESERVATION_DIALOG_VARIANT.FAILED:
			case RESERVATION_DIALOG_VARIANT.ROOM_NOT_AVAILABLE:
				return (
					<Button
						className="button-primary"
						key={'close-button'}
						variant="text"
						color="primary"
						onClick={primeButtonAction}
					>
						{t('ReservationDialog.SelectAnotherRoomButton')}
					</Button>
				);
			case RESERVATION_DIALOG_VARIANT.PRICE_CHANGED:
				return (
					<div className="action-buttons">
						<Button
							className="button-primary"
							key={'approve-button'}
							variant="text"
							color="primary"
							onClick={primeButtonAction}
						>
							{t('ReservationDialog.ConfirmContinueButton')}
						</Button>
						<Button
							className="button-secondary"
							key={'close-button'}
							variant="text"
							color="primary"
							onClick={() => this.goToHotel()}
						>
							{t('ReservationDialog.BackToHotelButton')}
						</Button>
					</div>
				);
			default:
				return null;
		}
	}

	getLongIdComponent() {
		const { variant, reservationId } = this.props;

		if (!reservationId) {
			return null;
		}

		switch (variant) {
			case RESERVATION_DIALOG_VARIANT.PRICE_CHANGED:
			case RESERVATION_DIALOG_VARIANT.PENDING:
			case RESERVATION_DIALOG_VARIANT.FAILED:
			case RESERVATION_DIALOG_VARIANT.ROOM_NOT_AVAILABLE:
				return <div className="id-text">ID: {reservationId}</div>;
			case RESERVATION_DIALOG_VARIANT.SUCCESS:
			case RESERVATION_DIALOG_VARIANT.QUOTE:
			case RESERVATION_DIALOG_VARIANT.OUT_OF_POLICY:
			default:
				return null;
		}
	}

	getFooter() {
		const { variant, profile, t } = this.props;
		const isLetStay = companyIds[profile.company.id] === COMPANY_NAME.LS;

		if (isLetStay) {
			return null;
		}

		switch (variant) {
			case RESERVATION_DIALOG_VARIANT.PENDING:
			case RESERVATION_DIALOG_VARIANT.FAILED:
				return (
					<div className="footer">
						<div className="footer-header">{t('ReservationDialog.FooterHeader')}</div>
						<div className="footer-numbers flex">
							<div>{t('ReservationDialog.FooterUSNumber')}</div>
							<div>|</div>
							<div>{t('ReservationDialog.FooterUKNumber')}</div>
							<div>|</div>
							<div>{t('ReservationDialog.FooterILNumber')}</div>
						</div>
					</div>
				);
			case RESERVATION_DIALOG_VARIANT.QUOTE:
			case RESERVATION_DIALOG_VARIANT.SUCCESS:
			case RESERVATION_DIALOG_VARIANT.ROOM_NOT_AVAILABLE:
			case RESERVATION_DIALOG_VARIANT.PRICE_CHANGED:
			case RESERVATION_DIALOG_VARIANT.OUT_OF_POLICY:
			default:
				return null;
		}
	}

	render() {
		const { variant } = this.props;
		let titleClasses = 'title';
		let imageClasses = 'image_header';
		switch (variant) {
			case RESERVATION_DIALOG_VARIANT.PENDING:
				titleClasses += ' green';
				imageClasses += ' pending-icon';
				break;
			case RESERVATION_DIALOG_VARIANT.SUCCESS:
			case RESERVATION_DIALOG_VARIANT.QUOTE:
			case RESERVATION_DIALOG_VARIANT.OUT_OF_POLICY:
				titleClasses += ' green';
				imageClasses += ' confirm-icon';
				break;
			case RESERVATION_DIALOG_VARIANT.ROOM_NOT_AVAILABLE:
			case RESERVATION_DIALOG_VARIANT.FAILED:
				titleClasses += ' red';
				imageClasses += ' error-icon';
				break;
			case RESERVATION_DIALOG_VARIANT.PRICE_CHANGED:
				imageClasses += ' pricing-icon';
				break;
			default:
				break;
		}
		return (
			<Dialog
				className={'reservation-dialog-main'}
				open={this.props.modalIsOpen}
				aria-describedby="alert-dialog-description"
				fullWidth={true}
			>
				<div className="reservation-dialog-container" data-testid="reservation-dialog">
					<div className={imageClasses} />
					<h3 className={titleClasses}>{this.getTitleText()}</h3>
					{this.getMainMessageComponent()}
					{this.getAdditionalMessageComponent()}
					{this.getActionButtonsComponent()}
					{this.getLongIdComponent()}
					{this.getFooter()}
				</div>
			</Dialog>
		);
	}
}

ReservationDialog.propTypes = {
	oldPrice: PropTypes.number,
	newPrice: PropTypes.number,
	shortId: PropTypes.string,
	reservationId: PropTypes.string,
	hotel: PropTypes.any.isRequired,
	variant: PropTypes.oneOf([
		RESERVATION_DIALOG_VARIANT.FAILED,
		RESERVATION_DIALOG_VARIANT.SUCCESS,
		RESERVATION_DIALOG_VARIANT.PENDING,
		RESERVATION_DIALOG_VARIANT.ROOM_NOT_AVAILABLE,
		RESERVATION_DIALOG_VARIANT.QUOTE,
		RESERVATION_DIALOG_VARIANT.OUT_OF_POLICY,
	]).isRequired,
	profile: PropTypes.object,
	primeButtonAction: PropTypes.func,
	secondaryButtonAction: PropTypes.func,
	modalIsOpen: PropTypes.bool.isRequired,
	tripName: PropTypes.string,
	isNonRefundable: PropTypes.bool,
	deal: PropTypes.any,
	t: PropTypes.func.isRequired,
};

export default withRouter(withTranslation()(ReservationDialog));
